/**
 * @deprecated Prefer injecting itinerantConfig into *InExhibition drawers.
 * Kept for simple M2M ExhibitionLinks (no pivot form).
 */
import { useSyncItinerantExhibitionGroup } from "@/api/related-fields";
import StyledDrawer from "@/components/StyledDrawer";
import { Button } from "@/components/ui/button";
import { CustomDialogFooter } from "@/components/ui/custom-dialog";
import { SheetClose } from "@/components/ui/sheet";
import {
  Exhibition,
  ItinerantExhibitionParentType,
} from "@/lib/types";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ItinerantExhibitionPanel,
  ItinerantStopDraft,
  itinerantStopFromExhibition,
} from "./ItinerantExhibitionPanel";

type ItinerantExhibitionDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parentType: ItinerantExhibitionParentType;
  parentId: string;
  groupId?: string | null;
  initialStops?: ItinerantStopDraft[];
  seedExhibition?: Exhibition;
  complexRelationKey?: string;
  showCatalogNumber?: boolean;
  showExemplaireExpose?: boolean;
  trigger?: React.ReactNode;
};

const ItinerantExhibitionDrawer = ({
  open,
  onOpenChange,
  parentType,
  parentId,
  groupId,
  initialStops,
  seedExhibition,
  complexRelationKey,
  showCatalogNumber = true,
  showExemplaireExpose = false,
  trigger,
}: ItinerantExhibitionDrawerProps) => {
  const { t } = useTranslation();
  const [stops, setStops] = useState<ItinerantStopDraft[]>([]);

  const { mutate: syncGroup, isPending } = useSyncItinerantExhibitionGroup(
    parentId,
    parentType,
    complexRelationKey,
  );

  const handleSave = () => {
    if (stops.length === 0) return;
    syncGroup(
      {
        groupId: groupId ?? undefined,
        parentType,
        parentId,
        stops: stops.map((s, index) => ({
          linkId: s.linkId,
          exhibitionId: s.exhibition.id,
          catalogNumber: s.catalogNumber,
          exemplaireExpose: s.exemplaireExpose,
          note: s.note,
          order: index,
        })),
      },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  return (
    <StyledDrawer
      title={groupId ? t("editLinkTitle") : t("createLinkTitle")}
      maxWidth="sm:max-w-6xl"
      trigger={trigger}
      sheetProps={{ open, onOpenChange }}
      footer={
        <CustomDialogFooter>
          <SheetClose asChild>
            <Button variant="outline" type="button">
              {t("cancel")}
            </Button>
          </SheetClose>
          <Button
            type="button"
            disabled={isPending || stops.length === 0}
            onClick={handleSave}
          >
            {groupId ? t("editLink") : t("createLink")}
          </Button>
        </CustomDialogFooter>
      }
    >
      <ItinerantExhibitionPanel
        open={open}
        seedExhibition={seedExhibition}
        initialStops={initialStops}
        groupId={groupId}
        parentType={parentType}
        parentId={parentId}
        stops={stops}
        onStopsChange={setStops}
        showCatalogNumber={showCatalogNumber}
        showExemplaireExpose={showExemplaireExpose}
      />
    </StyledDrawer>
  );
};

export default ItinerantExhibitionDrawer;
export { itinerantStopFromExhibition };
